<template>
  <div
    v-if="hasSuggestions || isFlashTradingActiveMode"
    class="flex mb-3 items-center"
  >
    <div class="inline-flex items-center mx-2">
      <NText
        v-if="hasSuggestions && !isFlashTrading"
        variant="overline"
        weight="medium"
        class="flex-none me-1"
      >
        {{ t('orders.flashTradingSuggestion') }}
      </NText>

      <NText
        v-if="isFlashTradingActiveMode"
        variant="overline"
        weight="medium"
        class="flex-none text-brand-primary me-1"
      >
        {{ t('orders.activeFlashTrading') }}
      </NText>

      <NegarInfoWithBorderIcon
        size="16"
        class="flex-none cursor-pointer"
        :class="{ 'text-brand-primary': isFlashTradingActiveMode }"
        @click="flashTradingInfoDialog"
      />
    </div>

    <div
      v-if="hasSuggestions && !isFlashTrading"
      ref="scrollableArea"
      class="flex overflow-x-scroll w-28"
    >
      <FlashTradingSuggestionsItem
        v-for="numberSuggestion of numberSuggestions"
        :key="numberSuggestion"
        :number-suggestion="numberSuggestion"
        @suggestion-number="handleSelectedSuggestionNumber"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useScroll } from '@vueuse/core';
import { NegarInfoWithBorderIcon } from '@negar/icons';
import { NText, useDialog } from '@negar/core';
import { Instrument } from '@libs/api-reference';

import {
  FlashTradingSuggestionsItem,
  FlashTradingDialogContent,
} from '@/modules/order';

type FormModel = {
  quantity?: number | null;
  price?: number | null;
  totalValue?: number | null;
  executedQuantity: number | null;
  validityType: number;
  validityDate: Date;
};

type FlashTrading = {
  formModel: FormModel;
  symbol: Instrument;
  maxQuantity: number | null;
  isFlashTrading: boolean;
  hasSuggestions: boolean;
};

type FlashTradingEmits = {
  (e: 'changeQuantity', value: number): void;
};

const emit = defineEmits<FlashTradingEmits>();

const props = defineProps<FlashTrading>();

const { t } = useI18n();
const scrollableArea = ref();
const openFlashTrading = ref(false);

const { add: addDialog } = useDialog();

useScroll(scrollableArea, { behavior: 'smooth' });

const numberSuggestions = computed(() => {
  return [...Array(5).keys()].map((factor) => {
    if (props.symbol.maximumOrderQuantity != null) {
      return props.symbol.maximumOrderQuantity * (factor + 1) * 10;
    }
  });
});

const handleSelectedSuggestionNumber = (itemRef, suggestionNumber) => {
  emit('changeQuantity', suggestionNumber);
};

const isFlashTradingActiveMode = computed(() => {
  if (
    props.symbol.maximumOrderQuantity &&
    props.formModel.quantity &&
    props.maxQuantity
  ) {
    return (
      props.formModel.quantity > props.symbol.maximumOrderQuantity &&
      props.formModel.quantity <= props.maxQuantity
    );
  }

  return false;
});

const flashTradingInfoDialog = () => {
  openFlashTrading.value = true;

  addDialog({
    title: t('orders.whatsFlashTrading'),
    bodyClass: '!w-1/3',
    content: computed(() => ({
      component: FlashTradingDialogContent,
      props: {},
    })),
  });
};
</script>
